import { useState, useRef, useCallback, useEffect } from "react";
import { Paperclip, Smile } from "lucide-react";
import "./DMInput.css";

interface DMInputProps {
  placeholder: string;
  onSend: (content: string) => void;
  onTypingStart: () => void;
  onTypingStop: () => void;
  onEmojiToggle: () => void;
}

export function DMInput({
  placeholder,
  onSend,
  onTypingStart,
  onTypingStop,
  onEmojiToggle,
}: DMInputProps) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function autoResize() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  }

  useEffect(() => {
    autoResize();
  }, [value]);

  const handleTyping = useCallback(() => {
    onTypingStart();
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      onTypingStop();
    }, 1000);
  }, [onTypingStart, onTypingStop]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) {
        onSend(value.trim());
        setValue("");
        onTypingStop();
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
        }
      }
    }
  }

  return (
    <div className="dm-input">
      <div className="dm-input__wrapper">
        <button className="dm-input__attach" aria-label="Attach file">
          <Paperclip size={18} />
        </button>
        <textarea
          ref={textareaRef}
          className="dm-input__textarea"
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            handleTyping();
          }}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <div className="dm-input__actions">
          <button
            className="dm-input__action-btn"
            onClick={onEmojiToggle}
            aria-label="Emoji"
          >
            <Smile size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
